// Settings › General (design/screens/settings-general.html): the organisation, locale, retention and privacy, feature
// flags, and the organisation edit with ETag / If-Match (UC-TENANCY-002); the patient titles (UC-PATIENTS-006: list, add,
// remove and restore for an Owner or Admin, the active list read-only for everyone else), and the consent text with Publish new
// version under the Control PIN for the Owner (UC-GOVERNANCE-008, SettingsConsentText.tsx), and Automatic dormancy — the Owner's
// rule, read-only for everyone else (UC-PATIENTS-029, SettingsDormancy.tsx).
import { COMMERCIAL_MODELS, PatientTitleInputSchema, type OrganisationPatch, type OrganisationView, type PatientTitle, type RetentionYears } from "@visionx/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router";
import { organisation } from "../api/endpoints";
import { titles } from "../api/endpoints.patients";
import { describeError, isApiError } from "../api/problem";
import { formatDate, formatDateTime, formatMoney, percentFromBp } from "../lib/format";
import { can, canEverywhere, useSessionView } from "../session/store";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { Alert, Chip, ErrorAlert, Field, KV, Loading, Modal, Panel, Select, TextInput, Toggle } from "../ui/bits";
import { NoAccessPanel, PageHead, SettingsTabs } from "./common";
import { ConsentTextPanel } from "./SettingsConsentText";
import { AutoDormancyPanel } from "./SettingsDormancy";
import { useTitles } from "./PatientsShared";

const retention = (v: RetentionYears): string => (v === "indefinite" ? "Indefinite" : `${v} years`);
const MODEL_NOTE: Record<(typeof COMMERCIAL_MODELS)[number], string> = {
  Group: "one ledger for all branches; a branch is a reporting tag in the accounting system, not a separate entity — no franchise fees",
  Independent: "one practice, one ledger",
  Franchisor: "sees linked franchisees' reports, audit and users — never their patients",
  Franchisee: "an independent ledger linked to a franchisor",
};
const FLAG_LABEL: Record<string, [string, string]> = {
  claims: ["Electronic medical-aid claims", "Submit claims through a switch instead of on each scheme's portal."],
  hoyalog: ["Hoyalog lab ordering", "Send lab orders straight into Hoya's Hoyalog."],
  franchise_fees: ["Franchise fees", "Calculate a franchise fee per branch. Not applicable while the commercial model is Group."],
  crm_connector: ["CRM connector", "Sync patients and, under consent, prescriptions to the practice's CRM."],
  self_service_portal: ["Patient self-service portal", "Online booking and document access for patients."],
  webauthn_stepup: ["WebAuthn sign-in", "Passkeys for staff sign-in on registered devices. The Control PIN remains the step-up for sensitive actions."],
  offline_writes: ["Offline writes", "Create orders and take payments while a device is offline. Today an offline device shows cached data only; new orders and payments wait for a connection."],
  api_clients: ["API clients", "Scoped API keys for integrations."],
};

export function SettingsGeneral() {
  const s = useSessionView();
  const qc = useQueryClient();
  const block = useWriteBlock();
  const online = block === null;
  const org = useQuery({ queryKey: ["organisation"], queryFn: () => organisation.get() });
  const flags = useQuery({ queryKey: ["feature-flags"], queryFn: () => organisation.featureFlags(), enabled: can(s, "tenancy.flags.write") });
  const [editing, setEditing] = useState(false);
  const o = org.data?.data;
  const branchNames = s?.switchableBranches.map((b) => b.name).join(", ");
  const now = new Date();

  return (
    <section className="screen" id="s-settings-general">
      <div className="content">
        <PageHead
          title="General"
          sub={`${o?.legalName ?? "Organisation"} · organisation-wide settings · every change here is recorded in the audit log`}
          actions={<>
            <Link className="btn" to="/settings/audit-log">Audit log</Link>
            {canEverywhere(s, "tenancy.settings.write") && <button className="btn primary" type="button" disabled={!o || !online} title={block ? WRITE_BLOCK_TITLE[block] : undefined} onClick={() => setEditing(true)}>Edit organisation</button>}
          </>}
        />
        <SettingsTabs />
        {org.isPending && <Loading what="organisation" />}
        {org.isError && (isApiError(org.error) && org.error.code === "PERMISSION_DENIED" ? <NoAccessPanel page="General settings" error={org.error} /> : <ErrorAlert error={org.error} />)}
        {o && (
          <>
            <div className="grid-2">
              <Panel title="Organisation" meta={`one legal entity · one VAT registration · ${s?.switchableBranches.length ?? 0} branches`}>
                <KV rows={[
                  ["Legal name", o.legalName],
                  ["Trading as", <>{o.tradingName ?? "—"}{branchNames ? <> · <Link to="/settings/branches">{branchNames}</Link></> : null}</>],
                  ["Status", <Chip key="st" tone={o.status === "Active" ? "ok" : "warn"} dot>{o.status}</Chip>],
                  ["Commercial model", <><Chip tone="accent">{o.commercialModel}</Chip><div className="small faint">{MODEL_NOTE[o.commercialModel]}</div></>],
                  ["Accounting", <>{o.accountingProvider === "None" ? "Not connected" : o.accountingProvider} · CRM {o.crmProvider === "None" ? "not connected" : o.crmProvider}</>],
                  ["Patient sharing", <>{o.patientSharingMode === "Tenant" ? "Every branch sees every patient" : "Patients are visible at their branch only"}</>],
                  ["Created", formatDate(o.createdAt)],
                ]} />
              </Panel>
              <Panel title="Locale" meta="applies to every screen and printed document" foot={<span className="help">Now: {formatDateTime(now)} in {o.timezone}.</span>}>
                <KV rows={[
                  ["Timezone", <span className="mono">{o.timezone}</span>],
                  // Each sample amount is one unbreakable run, so a narrow column never splits "R 1 234.56" across two lines.
                  ["Currency", <><span className="mono">{o.currency}</span> · displayed as <span className="mono nw">{formatMoney(123456)}</span> · negatives as <span className="mono nw">{formatMoney(-2000)}</span></>],
                  ["Locale", <><span className="mono">{o.locale}</span> · dates <span className="mono">{formatDate(now)}</span> · times <span className="mono">{formatDateTime(now).split(" ").pop()}</span></>],
                  ["VAT rate", <>{percentFromBp(o.vatRateBp)} · frozen on every document line at capture</>],
                  ["Cash denominations", <>Notes <span className="mono nw">{formatMoney(1000)}</span> – <span className="mono nw">{formatMoney(20000)}</span> · coins <span className="nw">10c – <span className="mono">{formatMoney(500)}</span></span></>],
                  ["ID validation", "SA ID checked (Luhn) · date of birth and gender derived from it"],
                ]} />
              </Panel>
            </div>
            <div className="grid-2">
              <ConsentTextPanel suspended={o.status !== "Active"} />
              <Panel title="Retention & privacy" meta="POPIA s14 · s26–27">
                <KV rows={[
                  ["Financial records", retention(o.retentionFinancialYears)],
                  ["Clinical records", <>{retention(o.retentionClinicalYears)}{o.retentionMinorUntilAge > 0 ? ` · minors until age ${o.retentionMinorUntilAge}` : ""}</>],
                  ["Logs", retention(o.retentionLogYears)],
                  ["Archiving", "Archived patients are excluded from search by default. Records are archived with a reason, never deleted."],
                  ["Special personal information", <>Clinical data is restricted by role and shared only under a consent purpose; every read is recorded in the <Link to="/settings/audit-log">audit log</Link></>],
                  ["Information Officer", o.informationOfficerName ? <>{o.informationOfficerName}{o.informationOfficerEmail ? ` · ${o.informationOfficerEmail}` : ""}{o.informationOfficerPhone ? ` · ${o.informationOfficerPhone}` : ""}<div className="small faint">receives access, correction and objection requests</div></> : <span className="faint">Not named yet</span>],
                ]} />
              </Panel>
            </div>
            <div className="grid-2">
            <Panel title="Step-up authorisation" meta="Control PIN · TEN-48">
              <KV rows={[
                ["PIN length", `${o.pinMinLength} to ${o.pinMaxLength} digits`],
                ["Discount threshold", <>{percentFromBp(o.discountPinThresholdPercentBp)}{o.discountPinThresholdCents > 0 ? <> or <span className="nw">{formatMoney(o.discountPinThresholdCents)}</span></> : ""} · {o.discountPinThresholdPercentBp === 0 ? "every discount needs an authoriser PIN" : "discounts above this need an authoriser PIN"}</>],
                ["Authoriser roles", o.stepupAuthoriserRoles ? o.stepupAuthoriserRoles.join(", ") : "Any active user"],
                ["Lockout", "5 wrong attempts within 15 min locks the PIN for 15 min · owner notified"],
                ["Session idle", `${o.sessionIdleMinutesDesktop} min desktop · ${o.sessionIdleMinutesTablet} min tablet · absolute session ${o.sessionAbsoluteHours} h`],
                ["Edit lock", `${o.editLockIdleMinutes} min idle · kiosk handover ${o.handoverIdleMinutes} min`],
              ]} />
            </Panel>
            <TitlesPanel />
            </div>
            <div className="grid-2">
              <AutoDormancyPanel />
            </div>
            {can(s, "tenancy.flags.write") && (
              <div className="panel">
                <div className="panel-head"><h2>Feature flags</h2><span className="meta">tenant-level{flags.data ? ` · ${Object.values(flags.data.data).filter(Boolean).length} on` : ""}</span></div>
                {flags.isError && <div className="panel-body"><ErrorAlert error={flags.error} /></div>}
                {flags.data && (
                  <div className="list">
                    {Object.entries(flags.data.data).map(([key, on]) => {
                      const [label, sub] = FLAG_LABEL[key] ?? [key, ""];
                      return (
                        <div key={key} className="item">
                          <div className="grow"><div className="primary">{label}</div><div className="sub">{sub}</div></div>
                          {!on && <Chip tone="neutral">Roadmap</Chip>}
                          <Toggle on={on} label={label} />
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="panel-foot"><span className="help">Roadmap features cannot be switched on here; they are enabled per tenant when they ship.</span></div>
              </div>
            )}
          </>
        )}
        {editing && o && org.data && (
          <EditOrganisation current={o} etag={org.data.etag} onClose={() => { setEditing(false); void qc.invalidateQueries({ queryKey: ["organisation"] }); }} onSaved={() => { setEditing(false); void qc.invalidateQueries({ queryKey: ["organisation"] }); }} />
        )}
      </div>
    </section>
  );
}

/**
 * UC-PATIENTS-006: the titles the patient form and the kiosk offer. An Owner or Admin (`patients.reference.write`) sees every
 * row from GET /patient-titles, seeded ones marked, adds one, removes one (deactivated, never deleted — BR-PATIENTS-025) and
 * restores a removed one by adding its code again (A1). Everyone else sees the active list from the effective settings.
 */
function TitlesPanel() {
  const s = useSessionView();
  const qc = useQueryClient();
  const block = useWriteBlock();
  const online = block === null;
  const blockTitle = block ? WRITE_BLOCK_TITLE[block] : undefined;
  const admin = can(s, "patients.reference.write");
  const list = useQuery({ queryKey: ["patient-titles", "admin"], queryFn: () => titles.list(), enabled: admin });
  const active = useTitles(!admin);
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [localErr, setLocalErr] = useState<{ code?: string | undefined; label?: string | undefined }>({});
  const [notice, setNotice] = useState<ReactNode | null>(null);
  const run = async (key: string, fn: () => Promise<ReactNode>) => {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      setNotice(await fn());
      void qc.invalidateQueries({ queryKey: ["patient-titles"] });
    } catch (err) { setError(err); } finally { setBusy(null); }
  };
  const add = (e: FormEvent) => {
    e.preventDefault();
    const parsed = PatientTitleInputSchema.safeParse({ code: code.trim(), label: label.trim() || code.trim() });
    if (!parsed.success) {
      const at = (f: string) => parsed.error.issues.find((i) => i.path[0] === f);
      setLocalErr({ code: at("code") ? "Letters and a dot only, up to 20 — e.g. Ms or Adv" : undefined, label: at("label") ? "Up to 40 characters" : undefined });
      return;
    }
    setLocalErr({});
    void run("add", async () => {
      const { data, status } = await titles.add(parsed.data);
      setCode("");
      setLabel("");
      return <Alert tone="ok"><b>{data.label} {status === 200 ? "restored" : "added"}.</b> The patient form and the kiosk offer it on their next load.</Alert>;
    });
  };
  const remove = (t: PatientTitle) => void run(t.code, async () => {
    await titles.remove(t.code);
    return <Alert tone="ok"><b>{t.label} removed.</b> Patients who have it keep it; it is no longer offered for new capture. Restore adds it back.</Alert>;
  });
  const restore = (t: PatientTitle) => void run(t.code, async () => {
    await titles.add({ code: t.code, label: t.label });
    return <Alert tone="ok"><b>{t.label} restored.</b> The patient form and the kiosk offer it again.</Alert>;
  });
  const fieldErr = (f: "code" | "label") => localErr[f] ?? (isApiError(error) ? error.field(f)?.message : undefined);

  if (!admin) {
    return (
      <Panel title="Patient titles" meta="offered on the patient form and the kiosk" foot={<span className="help">An Owner or Admin maintains the list.</span>}>
        {active.isPending ? <Loading what="titles" /> : active.isError ? <ErrorAlert error={active.error} /> : <div className="chips">{active.data.map((t) => <Chip key={t.code} tone="neutral">{t.label}</Chip>)}</div>}
      </Panel>
    );
  }
  const rows = list.data?.data.items ?? [];
  return (
    <div className="panel">
      <div className="panel-head"><h2>Patient titles</h2><span className="meta">offered on the patient form and the kiosk</span></div>
      {(notice || (error !== null && !fieldErr("code") && !fieldErr("label")) || list.isPending || list.isError) && (
        <div className="panel-body">
          {notice}
          {error !== null && !fieldErr("code") && !fieldErr("label") && <Alert tone="crit"><b>Not changed.</b> {describeError(error)}</Alert>}
          {list.isPending && <Loading what="titles" />}
          <ErrorAlert error={list.error} />
        </div>
      )}
      {rows.length > 0 && (
        <table>
          <thead><tr><th>Title</th><th>Label</th><th className="r">Order</th><th>Status</th><th className="r"></th></tr></thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.id}>
                <td><span className="mono">{t.code}</span>{t.isSeeded && <> <Chip tone="neutral">Seeded</Chip></>}</td>
                <td>{t.label}</td>
                <td className="r num">{t.sortOrder}</td>
                <td>{t.isActive ? <Chip tone="ok" dot>Offered</Chip> : <Chip tone="neutral">Removed</Chip>}</td>
                <td className="r">
                  {t.isActive
                    ? <button className="btn sm" type="button" disabled={!online || busy !== null} title={blockTitle} aria-label={`Remove ${t.label}`} onClick={() => remove(t)}>{busy === t.code ? "Removing…" : "Remove"}</button>
                    : <button className="btn sm" type="button" disabled={!online || busy !== null} title={blockTitle} aria-label={`Restore ${t.label}`} onClick={() => restore(t)}>{busy === t.code ? "Restoring…" : "Restore"}</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <form className="panel-foot" style={{ flexWrap: "wrap" }} onSubmit={add} aria-label="Add a title">
        <div className="title-add">
          <Field label="New title" htmlFor="title-code" error={fieldErr("code")}><TextInput id="title-code" value={code} maxLength={20} placeholder="Ms" mono error={!!fieldErr("code")} onChange={(e) => setCode(e.target.value)} /></Field>
          <Field label="Label" htmlFor="title-label" error={fieldErr("label")}><TextInput id="title-label" value={label} maxLength={40} placeholder="same as the title" onChange={(e) => setLabel(e.target.value)} /></Field>
          <button className="btn sm" type="submit" disabled={!online || busy !== null || !code.trim()} title={blockTitle}>{busy === "add" ? "Adding…" : "Add title"}</button>
        </div>
        <span className="help right">Removed titles stay on the patients who have them</span>
      </form>
    </div>
  );
}

type Draft = Record<string, string>;
const toDraft = (o: OrganisationView): Draft => ({
  legalName: o.legalName, tradingName: o.tradingName ?? "", vatRate: String(o.vatRateBp / 100), locale: o.locale, timezone: o.timezone,
  pinMinLength: String(o.pinMinLength), sessionIdleMinutesDesktop: String(o.sessionIdleMinutesDesktop), sessionIdleMinutesTablet: String(o.sessionIdleMinutesTablet), sessionAbsoluteHours: String(o.sessionAbsoluteHours),
  discountPercent: String(o.discountPinThresholdPercentBp / 100), discountRand: (o.discountPinThresholdCents / 100).toFixed(2), patientSharingMode: o.patientSharingMode,
  informationOfficerName: o.informationOfficerName ?? "", informationOfficerEmail: o.informationOfficerEmail ?? "", informationOfficerPhone: o.informationOfficerPhone ?? "",
});

function toPatch(o: OrganisationView, d: Draft): OrganisationPatch {
  const p: OrganisationPatch = {};
  const num = (s: string) => Number(s);
  const bp = (s: string) => Math.round(Number(s) * 100);
  if (d.legalName !== o.legalName) p.legalName = d.legalName!;
  if ((d.tradingName || null) !== o.tradingName) p.tradingName = d.tradingName || null;
  if (bp(d.vatRate!) !== o.vatRateBp) p.vatRateBp = bp(d.vatRate!) as never;
  if (d.locale !== o.locale) p.locale = d.locale!;
  if (d.timezone !== o.timezone) p.timezone = d.timezone!;
  if (num(d.pinMinLength!) !== o.pinMinLength) p.pinMinLength = num(d.pinMinLength!);
  if (num(d.sessionIdleMinutesDesktop!) !== o.sessionIdleMinutesDesktop) p.sessionIdleMinutesDesktop = num(d.sessionIdleMinutesDesktop!);
  if (num(d.sessionIdleMinutesTablet!) !== o.sessionIdleMinutesTablet) p.sessionIdleMinutesTablet = num(d.sessionIdleMinutesTablet!);
  if (num(d.sessionAbsoluteHours!) !== o.sessionAbsoluteHours) p.sessionAbsoluteHours = num(d.sessionAbsoluteHours!);
  if (bp(d.discountPercent!) !== o.discountPinThresholdPercentBp) p.discountPinThresholdPercentBp = bp(d.discountPercent!) as never;
  if (bp(d.discountRand!) !== o.discountPinThresholdCents) p.discountPinThresholdCents = bp(d.discountRand!) as never;
  if (d.patientSharingMode !== o.patientSharingMode) p.patientSharingMode = d.patientSharingMode as "Tenant" | "Branch";
  if ((d.informationOfficerName || null) !== o.informationOfficerName) p.informationOfficerName = d.informationOfficerName || null;
  if ((d.informationOfficerEmail || null) !== o.informationOfficerEmail) p.informationOfficerEmail = d.informationOfficerEmail || null;
  if ((d.informationOfficerPhone || null) !== o.informationOfficerPhone) p.informationOfficerPhone = d.informationOfficerPhone || null;
  return p;
}

function EditOrganisation({ current: live, etag: liveEtag, onClose, onSaved }: { current: OrganisationView; etag: string | null; onClose: () => void; onSaved: () => void }) {
  // The record and version this draft was made from (R-23): a refetch while the dialog is open — a window refocus, a
  // reconnect — never moves them, so a change someone else saved meanwhile is answered 409, never silently overwritten.
  const [{ current, etag }] = useState(() => ({ current: live, etag: liveEtag }));
  const [d, setD] = useState<Draft>(() => toDraft(current));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const set = (k: string) => (e: { target: { value: string } }) => setD((prev) => ({ ...prev, [k]: e.target.value }));
  const fieldErr = (path: string) => (isApiError(error) ? error.field(path)?.message : undefined);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const patch = toPatch(current, d);
    if (Object.keys(patch).length === 0) { onClose(); return; }
    if (!etag) { setError(new Error("The organisation was read without a version; reload and try again.")); return; }
    setBusy(true);
    setError(null);
    try { await organisation.patch(patch, etag); onSaved(); }
    catch (err) { setError(err); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Edit organisation" meta="audited before and after" wide onClose={onClose} onSubmit={(e) => void submit(e)} footer={<>
      <button className="btn" type="button" onClick={onClose}>Cancel</button>
      <button className="btn primary" type="submit" disabled={busy}>{busy ? "Saving…" : "Save changes"}</button>
    </>}>
      {isApiError(error) && error.code === "VERSION_CONFLICT" ? <Alert tone="warn"><b>The record has changed since you opened it.</b> Close, review the new values and try again.</Alert> : error !== null && <Alert tone="crit">{describeError(error)}</Alert>}
      <div className="grid-2">
        <Field label="Legal name" required error={fieldErr("legalName")}><TextInput required maxLength={200} value={d.legalName} onChange={set("legalName")} /></Field>
        <Field label="Trading as" error={fieldErr("tradingName")}><TextInput maxLength={200} value={d.tradingName} onChange={set("tradingName")} /></Field>
        <Field label="VAT rate (%)" error={fieldErr("vatRateBp")}><TextInput type="number" step="0.01" min={0} max={100} value={d.vatRate} onChange={set("vatRate")} mono /></Field>
        <Field label="Timezone" error={fieldErr("timezone")}><TextInput value={d.timezone} onChange={set("timezone")} mono /></Field>
        <Field label="Locale" error={fieldErr("locale")}><TextInput value={d.locale} onChange={set("locale")} mono /></Field>
        <Field label="Patient sharing"><Select value={d.patientSharingMode} onChange={set("patientSharingMode")}><option value="Tenant">Every branch sees every patient</option><option value="Branch">Per branch</option></Select></Field>
        <Field label="PIN length (minimum digits)" error={fieldErr("pinMinLength")}><TextInput type="number" min={4} max={6} value={d.pinMinLength} onChange={set("pinMinLength")} mono /></Field>
        <Field label="Discount threshold (%)" error={fieldErr("discountPinThresholdPercentBp")} help="Discounts above this need a Control PIN"><TextInput type="number" step="0.01" min={0} max={100} value={d.discountPercent} onChange={set("discountPercent")} mono /></Field>
        <Field label="Discount threshold (R)" error={fieldErr("discountPinThresholdCents")}><TextInput type="number" step="0.01" min={0} value={d.discountRand} onChange={set("discountRand")} mono /></Field>
        <Field label="Idle lock, desktop (minutes)" error={fieldErr("sessionIdleMinutesDesktop")}><TextInput type="number" min={1} value={d.sessionIdleMinutesDesktop} onChange={set("sessionIdleMinutesDesktop")} mono /></Field>
        <Field label="Idle lock, tablet (minutes)" error={fieldErr("sessionIdleMinutesTablet")}><TextInput type="number" min={1} value={d.sessionIdleMinutesTablet} onChange={set("sessionIdleMinutesTablet")} mono /></Field>
        <Field label="Absolute session (hours)" error={fieldErr("sessionAbsoluteHours")}><TextInput type="number" min={1} max={168} value={d.sessionAbsoluteHours} onChange={set("sessionAbsoluteHours")} mono /></Field>
        <Field label="Information Officer" error={fieldErr("informationOfficerName")}><TextInput maxLength={200} value={d.informationOfficerName} onChange={set("informationOfficerName")} /></Field>
        <Field label="Information Officer email" error={fieldErr("informationOfficerEmail")}><TextInput type="email" value={d.informationOfficerEmail} onChange={set("informationOfficerEmail")} /></Field>
        <Field label="Information Officer phone" error={fieldErr("informationOfficerPhone")}><TextInput value={d.informationOfficerPhone} onChange={set("informationOfficerPhone")} /></Field>
      </div>
    </Modal>
  );
}
